import { FC, Fragment, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  AllocationTableHeader,
  LeftHeader,
  SideColumn,
  Table,
} from 'components/time-entry-table'
import { daysOfWeek } from '../../../utils/daysOfWeek'
import { DurationCell } from './DurationCell'
import { useManagedGroupOutletContext } from '../../ManagedGroup'
import { useSignedInUsersWeekStartsOn } from '@hooks/useSignedInUsersWeekStartsOn'
import { IconButton, MenuButton } from 'components/buttons'
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons'

interface Props {
  projectName: string
  sourceCode: string
  tasks: SelectedTask[]
}

export const AllocationTable: FC<Props> = ({
  projectName,
  sourceCode,
  tasks,
}) => {
  const { t } = useTranslation()
  const {
    adminManagedGroup,
    setDuration,
    submitted,
    handleTaskClick,
    taskIdsWithEntries,
  } = useManagedGroupOutletContext()

  const [expanded, setExpanded] = useState(true)
  const weekStartsOn = useSignedInUsersWeekStartsOn()

  return (
    <div className="mt-6">
      <AllocationTableHeader
        expanded={expanded}
        onExpandChange={setExpanded}
        projectName={projectName}
        sourceCode={sourceCode}
      />
      {expanded && (
        <Table className="mt-4" weekStartsOn={weekStartsOn}>
          {tasks.map((task, index) => (
            <Fragment key={task.id}>
              <SideColumn side="left">
                <LeftHeader
                  key={task.id}
                  highlight={false}
                  labels={[task.name]}
                  rowIndex={index + 1}
                  showBorder={index !== 0}
                >
                  <MenuButton
                    button={({ disabled, toggleMenuHidden }) => (
                      <IconButton
                        icon={faEllipsisV}
                        onClick={toggleMenuHidden}
                        disabled={disabled}
                      />
                    )}
                    className="hidden group-hover:flex ml-auto"
                    menuItems={[
                      {
                        label: t('common.removeTask'),
                        onClick: () =>
                          handleTaskClick(task, { selected: false }),
                        disabled: taskIdsWithEntries.has(task.id),
                        tooltip: taskIdsWithEntries.has(task.id)
                          ? t('common.removeTaskDisabledTooltip')
                          : undefined,
                      },
                    ]}
                  />
                </LeftHeader>
              </SideColumn>
              {daysOfWeek.map((day) => {
                const dayTemplate =
                  adminManagedGroup.weekTemplate.dayTemplates[day]

                const duration =
                  dayTemplate.entries.find((entry) => entry.task.id === task.id)
                    ?.duration ?? 0

                return (
                  <DurationCell
                    key={`${task.id}-${day}`}
                    day={day}
                    duration={duration}
                    editable={dayTemplate.enabled}
                    isError={submitted && dayTemplate.errors.length > 0}
                    onChange={(duration) => {
                      setDuration(day, task, duration)
                    }}
                    rowIndex={index}
                  />
                )
              })}
            </Fragment>
          ))}
        </Table>
      )}
    </div>
  )
}
